import { inject, InjectionToken, Provider, Type } from '@angular/core';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AiUserFeedbackParams } from './models/ai-user-feedback-params';

export interface IPDialogService {
    open<ComponentType, ConfigType = MatDialogConfig, ResultType = any>(
        dialogComponent: Type<ComponentType>,
        config: ConfigType
    ): MatDialogRef<ComponentType, ResultType>;
}

export interface IAiFeedbackService {
    saveAiUserFeedback(aiUserFeedbackParams: AiUserFeedbackParams): Observable<unknown>;
}

export const P_AI_DIALOG_SERVICE = new InjectionToken<IPDialogService>('Dialog service for p-ai-feedback component');

export const P_AI_FEEDBACK_DIALOG_COMPONENT = new InjectionToken<Type<unknown>>('Feedback dialog component for p-ai-feedback');

export const P_AI_FEEDBACK_SERVICE = new InjectionToken<IAiFeedbackService>('AI feedback service for p-ai-feedback component');

export function injectPAiDialogService(): IPDialogService {
    const service = inject(P_AI_DIALOG_SERVICE, { optional: true });
    if (!service) {
        throw new Error(
            'P_AI_DIALOG_SERVICE is not provided. Make sure you call the providePAiFeedback() function in your root module and specify the dialogService.'
        );
    }
    return service;
}

export function injectPAiFeedbackDialogComponent(): Type<unknown> {
    const component = inject(P_AI_FEEDBACK_DIALOG_COMPONENT, { optional: true });
    if (!component) {
        throw new Error(
            'P_AI_FEEDBACK_DIALOG_COMPONENT is not provided. Make sure you call the providePAiFeedback() function in your root module and specify the feedbackDialogComponent.'
        );
    }
    return component;
}

export function injectPAiFeedbackService(): IAiFeedbackService {
    const service = inject(P_AI_FEEDBACK_SERVICE, { optional: true });
    if (!service) {
        throw new Error(
            'P_AI_FEEDBACK_SERVICE is not provided. Make sure you call the providePAiFeedback() function in your root module and specify the feedbackService.'
        );
    }
    return service;
}

export interface PAiFeedbackConfig {
    dialogService: Type<IPDialogService>;
    feedbackDialogComponent: Type<unknown>;
    feedbackService: Type<IAiFeedbackService>;
}

export function providePAiFeedback(config: PAiFeedbackConfig): Provider[] {
    return [
        { provide: P_AI_DIALOG_SERVICE, useExisting: config.dialogService },
        { provide: P_AI_FEEDBACK_DIALOG_COMPONENT, useValue: config.feedbackDialogComponent },
        { provide: P_AI_FEEDBACK_SERVICE, useExisting: config.feedbackService }
    ];
}
